import { QueueEntry } from '@/types/queue.types';
import { Credentials } from '@/types/auth.types';
import { websocketService } from './websocketService';
import { debug } from '@/config/constants';
import { configService } from './configService';
import { SignatureV4 } from '@aws-sdk/signature-v4';
import { Sha256 } from '@aws-crypto/sha256-js';
import { HttpRequest } from '@aws-sdk/protocol-http';

class QueueService {
  private apiEndpoint: string = '';

  private getApiEndpoint(): string {
    if (!this.apiEndpoint) {
      const config = configService.getConfig();
      if (!config) {
        throw new Error('Configuration not loaded');
      }
      this.apiEndpoint = config.API_ENDPOINT;
    }
    return this.apiEndpoint;
  }

  /**
   * Make a signed HTTP request to API Gateway
   */
  private async makeSignedRequest(
    endpoint: string,
    method: string,
    credentials: Credentials,
    body?: any
  ): Promise<Response> {
    const config = configService.getConfig();
    if (!config) {
      throw new Error('Configuration not loaded');
    }

    const url = new URL(`${this.getApiEndpoint()}${endpoint}`);
    
    const request = new HttpRequest({
      method,
      protocol: url.protocol,
      hostname: url.hostname,
      path: url.pathname,
      headers: {
        'Content-Type': 'application/json',
        'host': url.hostname,
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const signer = new SignatureV4({
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
      region: config.REGION,
      service: 'execute-api',
      sha256: Sha256,
    });

    const signedRequest = await signer.sign(request);

    return fetch(`${url.protocol}//${url.hostname}${url.pathname}`, {
      method: signedRequest.method,
      headers: signedRequest.headers as HeadersInit,
      body: signedRequest.body,
    });
  }

  async joinQueue(userId: string, credentials: Credentials, featureName?: string): Promise<QueueEntry> {
    try {
      // Generate a unique session ID for this queue entry
      const uniqueId = `${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await this.makeSignedRequest(
        'queue/join',
        'POST',
        credentials,
        {
          userId: uniqueId,
          featureName,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to join queue: ${response.statusText}`);
      }

      const data = await response.json();
      debug.log('Joined queue:', data);

      // Connect to WebSocket for real-time updates
      const config = configService.getConfig();
      if (config?.WS_API_ENDPOINT) {
        try {
          await websocketService.connect(config.WS_API_ENDPOINT, data.queueId);
        } catch (wsError) {
          debug.warn('WebSocket connection failed, will use polling fallback:', wsError);
        }
      }

      return data as QueueEntry;
    } catch (error) {
      console.error('Error joining queue:', error);
      throw error;
    }
  }

  async leaveQueue(queueId: string, credentials: Credentials): Promise<void> {
    try {
      // Disconnect WebSocket first
      websocketService.disconnect();

      const response = await this.makeSignedRequest(
        `queue/leave/${queueId}`,
        'DELETE',
        credentials
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to leave queue: ${response.statusText}`);
      }

      debug.log('Left queue successfully');
    } catch (error) {
      console.error('Error leaving queue:', error);
      throw error;
    }
  }

  async getQueueStatus(queueId: string, credentials: Credentials): Promise<QueueEntry | null> {
    try {
      const response = await this.makeSignedRequest(
        `queue/status/${queueId}`,
        'GET',
        credentials
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `Failed to get queue status: ${response.statusText}`);
      }

      const data = await response.json();
      return data as QueueEntry;
    } catch (error) {
      console.error('Error getting queue status:', error);
      throw error;
    }
  }
}

export const queueService = new QueueService();
